import React, { useState, useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { addReaction } from '../../store/slices/commentsSlice';
import { api } from '../../services/api';

interface ReactionType {
  type: string;
  emoji: string;
  label: string;
  order: number;
}

interface CommentReactionsProps {
  commentId: string;
  reactionSummary?: Record<string, number>;
  userReactions?: Record<string, boolean>;
}

const CommentReactions: React.FC<CommentReactionsProps> = ({
  commentId,
  reactionSummary = {},
  userReactions = {},
}) => {
  const dispatch = useAppDispatch();
  const { user, accessToken } = useAppSelector((state) => state.auth);
  const [reactionTypes, setReactionTypes] = useState<ReactionType[]>([]);
  const [showPicker, setShowPicker] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const loadReactionTypes = async () => {
      try {
        const types = await api.reactionTypes.getAll(accessToken || undefined);
        setReactionTypes(types.sort((a: ReactionType, b: ReactionType) => a.order - b.order));
      } catch (error) {
        console.error('Error loading reaction types:', error);
      }
    };
    loadReactionTypes();
  }, [accessToken]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        pickerRef.current &&
        buttonRef.current &&
        !pickerRef.current.contains(event.target as Node) &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setShowPicker(false);
      }
    };

    if (showPicker) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showPicker]);

  const handleReaction = async (type: string) => {
    if (!user) return;
    try {
      await dispatch(addReaction({ commentId, type }));
      setShowPicker(false);
    } catch (error) {
      console.error('Error adding reaction:', error);
    }
  };

  const hasReactions = Object.keys(reactionSummary).length > 0;

  // Get emoji for reaction type
  const getEmoji = (type: string) => {
    const reactionType = reactionTypes.find((rt) => rt.type === type);
    return reactionType ? reactionType.emoji : type;
  };

  // Get label for reaction type
  const getLabel = (type: string) => {
    const reactionType = reactionTypes.find((rt) => rt.type === type);
    return reactionType ? reactionType.label : type;
  };

  return (
    <div className="flex items-center gap-2">
      {/* Reaction summary */}
      {hasReactions && (
        <div className="flex items-center gap-1 flex-wrap">
          {Object.entries(reactionSummary).map(([type, count]) => (
            <button
              key={type}
              onClick={() => handleReaction(type)}
              className={`flex items-center gap-1 px-2 py-1 text-sm rounded-full transition-colors ${
                userReactions[type]
                  ? 'bg-[var(--color-brand-100)] text-[var(--color-brand-700)] border border-[var(--color-brand-300)]'
                  : 'bg-[var(--color-neutral-100)] text-[var(--color-neutral-700)] hover:bg-[var(--color-neutral-200)] border border-[var(--color-neutral-300)]'
              }`}
              aria-label={`${getLabel(type)}: ${count}`}
              title={`${getLabel(type)}: ${count}`}
            >
              <span className="text-base">{getEmoji(type)}</span>
              <span className="font-medium">{count}</span>
            </button>
          ))}
        </div>
      )}

      {/* Reaction picker */}
      {user && reactionTypes.length > 0 && (
        <div className="relative">
          <button
            ref={buttonRef}
            type="button"
            onClick={() => setShowPicker(!showPicker)}
            className="p-1 hover:bg-[var(--color-neutral-100)] rounded transition-colors text-[var(--color-subtext-color)] hover:text-[var(--color-default-font)]"
            aria-label="Add reaction"
            title="Add reaction"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </button>
          {showPicker && (
            <div
              ref={pickerRef}
              className="absolute bottom-full left-0 mb-1 bg-[var(--color-default-background)] border border-[var(--color-neutral-border)] rounded-lg shadow-lg p-2 gap-1 z-10 flex flex-wrap"
              style={{ width: 'max-content', minWidth: '200px' }}
            >
              {reactionTypes.map((reaction) => (
                <button
                  key={reaction.type}
                  type="button"
                  onClick={() => handleReaction(reaction.type)}
                  className={`p-2 rounded hover:bg-[var(--color-neutral-100)] transition-colors text-xl ${
                    userReactions[reaction.type] ? 'bg-[var(--color-brand-50)]' : ''
                  }`}
                  aria-label={reaction.label}
                  title={reaction.label}
                >
                  {reaction.emoji}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CommentReactions;
